/* ============================================================
   useCapabilities — droits du plan courant, côté front
   ------------------------------------------------------------
   Chantier « palier Découverte 9,99 € » (30/07/2026).

   La matrice de droits vit UNIQUEMENT côté backend
   (backend/config/planCapabilities.js). Le front la CONSOMME via
   GET /api/restaurants (champ `capabilities`) — il ne la duplique jamais.

   ⚠️ Le front ne fait que masquer/verrouiller l'UI. Chaque route sensible
   renvoie de toute façon un 403 côté serveur (requireCapability) : un
   contournement de l'UI ne donne accès à rien.

   Cache module-level + promesse partagée : les onglets se montent en
   parallèle, on ne veut qu'UN seul appel réseau par session de navigation.
   ============================================================ */
import { useEffect, useState } from 'react';
import { apiGet } from '../utils/api-client';

let cache = null;        // dernière valeur connue { capabilities, user_plan, account_status }
let inflight = null;     // promesse en cours, partagée entre tous les consommateurs

// Marqueurs des URL de retour de paiement (cf. success_url des sessions Stripe
// dans backend/routes/stripe.js et backend/routes/account.js).
const PAYMENT_RETURN_MARKERS = ['paiement=success', 'reactivated=1'];

// Délais de re-vérification après un paiement, le temps que le webhook Stripe
// enregistre le plan. Deux tentatives suffisent en pratique ; au-delà, un
// rechargement de page prendra le relais.
const WEBHOOK_RETRY_DELAYS_MS = [2500, 7000];

function isPaymentReturn() {
  if (typeof window === 'undefined') return false;
  const search = window.location.search || '';
  return PAYMENT_RETURN_MARKERS.some((m) => search.includes(m));
}

/** Vide le cache — à appeler après un changement de plan (retour de Stripe). */
export function invalidateCapabilities() {
  cache = null;
  inflight = null;
}

async function fetchCapabilities() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await apiGet('/api/restaurants/');
      cache = {
        capabilities: data?.capabilities || null,
        userPlan: data?.user_plan || null,
        accountStatus: data?.account_status || null,
      };
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * @returns {{ capabilities: object|null, userPlan: string|null,
 *             accountStatus: string|null, loading: boolean,
 *             has: (capability: string) => boolean }}
 *
 * `has()` renvoie true tant que les capacités ne sont pas chargées : on
 * n'affiche PAS un cadenas à un abonné Pro pendant le chargement (faux
 * positif très visible). Le backend reste la barrière réelle.
 */
export default function useCapabilities() {
  const [state, setState] = useState(() => ({
    capabilities: cache?.capabilities || null,
    userPlan: cache?.userPlan || null,
    accountStatus: cache?.accountStatus || null,
    loading: !cache,
  }));

  useEffect(() => {
    let cancelled = false;
    const timers = [];

    const apply = (res) => {
      if (cancelled) return;
      setState({
        capabilities: res.capabilities,
        userPlan: res.userPlan,
        accountStatus: res.accountStatus,
        loading: false,
      });
    };
    const onError = () => {
      // Échec réseau / 401 : on ne verrouille rien côté UI (fail-open visuel).
      // Le backend, lui, reste fail-closed.
      if (!cancelled) setState((s) => ({ ...s, loading: false }));
    };

    // Retour de Stripe Checkout : le webhook qui enregistre le plan est
    // ASYNCHRONE. L'utilisateur peut atterrir sur /dashboard avant qu'il soit
    // traité et voir ses onglets encore verrouillés alors qu'il vient de payer.
    // On re-interroge donc l'API quelques secondes plus tard, sans exiger de
    // rafraîchissement manuel. Souscrire doit débloquer immédiatement.
    if (isPaymentReturn()) {
      invalidateCapabilities();
      const recheck = () => {
        if (cancelled) return;
        invalidateCapabilities();
        fetchCapabilities().then(apply).catch(() => {});
      };
      WEBHOOK_RETRY_DELAYS_MS.forEach((delay) => {
        timers.push(setTimeout(recheck, delay));
      });
    }

    if (!cache) fetchCapabilities().then(apply).catch(onError);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  const has = (capability) => {
    if (!capability) return true;
    if (!state.capabilities) return true; // pas encore chargé ou indisponible
    return state.capabilities[capability] === true;
  };

  return { ...state, has };
}
